import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { api, errMsg } from "../../lib/staffApi";
import SignOutButton from "../SignOutButton";

const box = "mx-auto mt-16 max-w-md rounded-2xl border border-border bg-card p-6";
const input = "w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm";

/* Every staff page sits behind this. It loads /admin/session; on 403 staff_2fa_required it walks the user through
   authenticator setup (with backup codes) or a step-up code. The server then sets a 2FA staff session cookie. */
export default function StaffGate({ children }) {
  const [state, setState] = useState({ phase: "loading" });
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null);
  const [backup, setBackup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get("/admin/session")
      .then(({ data }) => setState({ phase: "ok", session: data }))
      .catch((e) => {
        const s = e?.response?.status;
        const d = e?.response?.data?.detail;
        if (s === 401) setState({ phase: "signin" });
        else if (s === 403 && d?.code === "staff_2fa_required") setState({ phase: d.enrolled ? "stepup" : "enrol" });
        else if (s === 403) setState({ phase: "denied" });
        else setState({ phase: "error", msg: errMsg(e) });
      });
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const startSetup = () => run(async () => { const { data } = await api.post("/admin/2fa/setup"); setSetup(data); });
  const confirm = () => run(async () => {
    const { data } = await api.post("/admin/2fa/confirm", { code: code.trim() });
    setBackup(data.backup_codes); setCode(""); setState({ phase: "stepup" });
  });
  const stepUp = () => run(async () => { await api.post("/admin/step-up", { code: code.trim() }); setCode(""); load(); });

  if (state.phase === "loading") return <div className="pt-32 text-center text-sm text-muted-foreground">Loading…</div>;
  if (state.phase === "signin") return <div className={box}>Please <Link className="underline" to="/admin/login">sign in</Link> first.</div>;
  if (state.phase === "denied") return <div className={box} data-testid="staff-denied"><SignOutButton />Staff only.</div>;
  if (state.phase === "error") return <div className={box}><SignOutButton />{state.msg}</div>;
  if (state.phase === "ok") return <><SignOutButton />{children({ ...state.session, reload: load })}</>;

  return (
    <div className={box} data-testid="staff-2fa-gate">
      <SignOutButton />
      <h1 className="font-display text-xl font-semibold">Staff sign-in</h1>
      {backup && (
        <div className="mt-3 rounded-md border border-amber-500 p-3 font-mono text-xs" data-testid="staff-backup-codes">
          <div className="mb-1 font-semibold">Save these backup codes now. Each works once; they won't be shown again.</div>
          <div className="grid grid-cols-2 gap-1">{backup.map((c) => <span key={c}>{c}</span>)}</div>
        </div>
      )}
      {state.phase === "enrol" && !setup && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">Staff tools need an authenticator app (Google Authenticator, 1Password, Authy…).</p>
          <Button className="mt-4" disabled={busy} onClick={startSetup} data-testid="staff-2fa-start">Set up authenticator</Button>
        </>
      )}
      {state.phase === "enrol" && setup && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">Add this key to your authenticator app (“enter a setup key”, time-based):</p>
          <div className="mt-2 break-all rounded bg-muted p-2 font-mono text-sm" data-testid="staff-2fa-secret">{setup.secret}</div>
          <a href={setup.otpauth_url} className="text-xs underline">Open in an authenticator app on this device</a>
          <p className="mt-4 text-sm text-muted-foreground">Then type the 6-digit code it shows:</p>
          <input className={input} value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" />
          <Button className="mt-3" disabled={busy || code.trim().length < 6} onClick={confirm}>Confirm</Button>
        </>
      )}
      {state.phase === "stepup" && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">{state.msg || "Enter the code from your authenticator app, or a backup code."}</p>
          <input className={`${input} mt-2`} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" placeholder="123456 or backup code" data-testid="staff-2fa-code" />
          <Button className="mt-3" disabled={busy || code.trim().length < 6} onClick={stepUp} data-testid="staff-2fa-submit">Continue</Button>
        </>
      )}
      {err && <p className="mt-3 text-sm text-red-600" data-testid="staff-2fa-error">{err}</p>}
    </div>
  );
}
